import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faWandMagicSparkles,
    faPaperPlane,
    faPlus,
    faXmark,
    faFileExcel,
    faPencil,
    faSpinner,
    faTriangleExclamation,
    faClockRotateLeft,
} from '@fortawesome/free-solid-svg-icons';
import { useFlashMessage } from '../../components/FlashMessage';
import { extractApiError } from '../../utils/errorMessages';
import {
    fetchCatalogue,
    askReporting,
    fetchAskSessions,
    fetchAskSession,
    deleteAskSession,
    previewRecipe,
    downloadPreview,
    createReport,
} from '../../services/reportingService';
import ResultTable from './ResultTable';

// Three requests that show the shape of a good one, not just the subject.
const EXAMPLES = [
    'Unpaid customer invoices, biggest balance first',
    'Everything bought from suppliers this month, by port',
    'Deliveries not invoiced yet',
];

/** Only the parts of a period the server reads back. */
const runPeriod = (period) => (period ? {
    key: period.key,
    from: period.from,
    to: period.to,
    include_undated: Boolean(period.include_undated),
} : undefined);

/**
 * Ask for a report in your own words.
 *
 * The chat is on the right and the answer — the actual table — is on the left,
 * where it stays while the conversation carries on. Nothing is decided here:
 * the request goes to the server, which writes the report and runs it under
 * this person's own access, and this screen shows what came back. An answer
 * that could not be turned into a report comes back as words only, and the last
 * table stays where it was rather than vanishing.
 */
const AskAi = ({ canBuild = false, onOpenInBuilder, onSaved }) => {
    const { showSuccess } = useFlashMessage();
    const [groupNames, setGroupNames] = useState([]);
    const [sessions, setSessions] = useState([]);
    const [sessionId, setSessionId] = useState(null);
    const [turns, setTurns] = useState([]);
    const [input, setInput] = useState('');
    const [sending, setSending] = useState(false);

    const [answer, setAnswer] = useState(null);
    const [answerBusy, setAnswerBusy] = useState('');
    const [saveName, setSaveName] = useState('');
    const [saveError, setSaveError] = useState('');
    const [savingOpen, setSavingOpen] = useState(false);

    const messagesRef = useRef(null);

    const loadSessions = useCallback(async () => {
        try {
            const data = await fetchAskSessions();
            setSessions(data.sessions || []);
        } catch (err) {
            // Recent requests are a convenience; a failure here must not take
            // the chat down with it.
            setSessions([]);
        }
    }, []);

    useEffect(() => {
        let alive = true;
        fetchCatalogue()
            .then((data) => {
                if (!alive) return;
                const names = [];
                (data.subjects || []).forEach((subject) => {
                    (subject.groups || []).forEach((group) => {
                        if (!names.includes(group.name)) names.push(group.name);
                    });
                });
                setGroupNames(names);
            })
            .catch(() => { /* the examples still stand on their own */ });
        loadSessions();
        return () => { alive = false; };
    }, [loadSessions]);

    useEffect(() => {
        if (messagesRef.current) {
            messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
        }
    }, [turns, sending]);

    const send = async (text) => {
        const message = String(text ?? input).trim();
        if (!message || sending) return;
        setInput('');
        setTurns((current) => [...current, { role: 'user', text: message }]);
        setSending(true);
        try {
            const data = await askReporting({
                message,
                ...(sessionId ? { session_id: sessionId } : {}),
            });
            setSessionId(data.session_id);
            setTurns((current) => [...current, {
                role: 'assistant', text: data.reply, ok: data.ok, title: data.title,
            }]);
            if (data.ok && data.result) {
                setAnswer({
                    title: data.title || 'Your report',
                    recipe: data.recipe,
                    used: data.used,
                    result: data.result,
                });
                setSaveName(data.title || '');
                setSaveError('');
                setSavingOpen(false);
            }
            loadSessions();
        } catch (err) {
            setTurns((current) => [...current, {
                role: 'assistant',
                text: extractApiError(err, 'That request could not be answered. Try wording it differently.'),
                ok: false,
            }]);
        } finally {
            setSending(false);
        }
    };

    const startNewChat = () => {
        setSessionId(null);
        setTurns([]);
        setAnswer(null);
        setInput('');
    };

    const openSession = async (session) => {
        setSending(true);
        try {
            const data = await fetchAskSession(session.id);
            setSessionId(data.id);
            setTurns(data.turns || []);
            setAnswer(null);
            const last = [...(data.turns || [])].reverse()
                .find((turn) => turn.role === 'assistant' && turn.recipe);
            if (last) {
                const result = await previewRecipe({
                    ...last.recipe,
                    ...(last.period ? { period: runPeriod(last.period) } : {}),
                });
                setAnswer({
                    title: last.title || data.title,
                    recipe: last.recipe,
                    used: last.used,
                    result,
                });
                setSaveName(last.title || data.title || '');
            }
        } catch (err) {
            setTurns((current) => [...current, {
                role: 'assistant',
                text: extractApiError(err, 'That request could not be re-opened.'),
                ok: false,
            }]);
        } finally {
            setSending(false);
        }
    };

    const removeSession = async (session) => {
        if (!window.confirm(`Delete "${session.title}"? This cannot be undone.`)) return;
        try {
            await deleteAskSession(session.id);
            if (sessionId === session.id) startNewChat();
            loadSessions();
        } catch (err) {
            setSaveError(extractApiError(err, 'Could not delete that request.'));
        }
    };

    const handleDownload = async () => {
        setAnswerBusy('download');
        setSaveError('');
        try {
            const fileName = await downloadPreview({
                ...answer.recipe,
                name: answer.title,
                period: runPeriod(answer.result?.period),
            }, 'xlsx');
            showSuccess(`Saved ${fileName}.`);
        } catch (err) {
            setSaveError(extractApiError(err, 'Could not download this report.'));
        } finally {
            setAnswerBusy('');
        }
    };

    const handleSave = async () => {
        setAnswerBusy('save');
        setSaveError('');
        try {
            await createReport({ ...answer.recipe, name: saveName, origin: 'ai' });
            showSuccess('Saved to My reports.');
            setSavingOpen(false);
            onSaved();
        } catch (err) {
            // A name clash comes back as a sentence naming the report they
            // already have — shown here so they can retype without losing the answer.
            setSaveError(extractApiError(err, 'Could not save this report.'));
            setSavingOpen(true);
        } finally {
            setAnswerBusy('');
        }
    };

    return (
        <div className="rc-ask">
            {/* LEFT — the working area: what you can ask, or the answer itself */}
            <div className="rc-panel rc-ask-work">
                {answer ? (
                    <>
                        <div className="rc-ask-answer-head">
                            <h3 className="rc-ask-answer-title">{answer.title}</h3>
                            <div className="rc-ask-answer-actions">
                                <button
                                    type="button"
                                    className="rc-btn-ghost"
                                    onClick={handleDownload}
                                    disabled={Boolean(answerBusy)}
                                >
                                    <FontAwesomeIcon
                                        icon={answerBusy === 'download' ? faSpinner : faFileExcel}
                                        spin={answerBusy === 'download'}
                                    />
                                    {answerBusy === 'download' ? 'Preparing…' : 'Download Excel'}
                                </button>
                                <button
                                    type="button"
                                    className="rc-btn-ghost"
                                    onClick={() => (savingOpen ? handleSave() : setSavingOpen(true))}
                                    disabled={Boolean(answerBusy)}
                                >
                                    <FontAwesomeIcon
                                        icon={answerBusy === 'save' ? faSpinner : faPlus}
                                        spin={answerBusy === 'save'}
                                    />
                                    {answerBusy === 'save' ? 'Saving…' : 'Save to My reports'}
                                </button>
                                {canBuild && (
                                    <button
                                        type="button"
                                        className="rc-btn-ghost"
                                        onClick={() => onOpenInBuilder({ name: answer.title, recipe: answer.recipe })}
                                        disabled={Boolean(answerBusy)}
                                    >
                                        <FontAwesomeIcon icon={faPencil} /> Open in builder
                                    </button>
                                )}
                            </div>
                        </div>

                        {savingOpen && (
                            <div className="rc-ask-save-row">
                                <label className="rc-field-label" htmlFor="rc-ask-save-name">
                                    Save it as
                                </label>
                                <input
                                    id="rc-ask-save-name"
                                    type="text"
                                    className="rc-input rc-grow"
                                    value={saveName}
                                    onChange={(e) => setSaveName(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
                                />
                                <button
                                    type="button"
                                    className="rc-link-btn"
                                    onClick={() => { setSavingOpen(false); setSaveError(''); }}
                                >
                                    Cancel
                                </button>
                            </div>
                        )}

                        {saveError && (
                            <div className="rc-error rc-error-spaced">
                                <FontAwesomeIcon icon={faTriangleExclamation} /> {saveError}
                            </div>
                        )}

                        <ResultTable
                            result={answer.result}
                            caption={answer.title}
                            emptyText="Nothing matched that request. Try a wider period or fewer conditions."
                        />

                        {answer.used && (
                            <div className="rc-used">
                                <div className="rc-used-label">Built from these data points</div>
                                <div className="rc-used-chips">
                                    {(answer.used.data_points || []).map((label) => (
                                        <span key={label} className="rc-used-chip">{label}</span>
                                    ))}
                                </div>
                                {(answer.used.filters || []).length > 0 && (
                                    <div className="rc-used-filters">
                                        {(answer.used.filters || []).join(' · ')}
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        <h3 className="rc-ask-answer-title">What you can ask about</h3>
                        {groupNames.length > 0 && (
                            <div className="rc-used-chips">
                                {groupNames.map((name) => (
                                    <span key={name} className="rc-used-chip">{name}</span>
                                ))}
                            </div>
                        )}
                        <div className="rc-ask-examples">
                            {EXAMPLES.map((example) => (
                                <button
                                    key={example}
                                    type="button"
                                    className="rc-example-chip"
                                    onClick={() => send(example)}
                                    disabled={sending}
                                >
                                    {example}
                                </button>
                            ))}
                        </div>

                        <div className="rc-section">
                            <div className="rc-section-title">
                                <FontAwesomeIcon icon={faClockRotateLeft} /> Recent requests
                            </div>
                            {sessions.length === 0 ? (
                                <div className="rc-panel-message">
                                    Your past requests will be listed here so you can pick one up again.
                                </div>
                            ) : (
                                <ul className="rc-session-list">
                                    {sessions.map((session) => (
                                        <li key={session.id} className="rc-session-row">
                                            <button
                                                type="button"
                                                className="rc-session-open"
                                                onClick={() => openSession(session)}
                                                disabled={sending}
                                            >
                                                {session.title}
                                            </button>
                                            <button
                                                type="button"
                                                className="rc-chip-icon-btn"
                                                aria-label={`Delete ${session.title}`}
                                                onClick={() => removeSession(session)}
                                            >
                                                <FontAwesomeIcon icon={faXmark} />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* RIGHT — the conversation */}
            <div className="rc-panel rc-ask-chat">
                <div className="rc-chat-head">
                    <FontAwesomeIcon icon={faWandMagicSparkles} className="rc-chat-head-icon" />
                    <span className="rc-chat-head-title">Reporting AI</span>
                    <button type="button" className="rc-link-btn" onClick={startNewChat}>
                        <FontAwesomeIcon icon={faPlus} /> New chat
                    </button>
                </div>

                <div className="rc-chat-messages" ref={messagesRef}>
                    {turns.length === 0 && !sending && (
                        <div className="rc-panel-message">
                            Describe the report you need in your own words. Pick one of the
                            examples on the left if you would rather start from something.
                        </div>
                    )}
                    {turns.map((turn, index) => (
                        <div
                            key={index}
                            className={`rc-msg ${turn.role === 'user' ? 'rc-msg-mine' : 'rc-msg-reply'}`}
                        >
                            <div className="rc-msg-bubble">{turn.text}</div>
                        </div>
                    ))}
                    {sending && (
                        <div className="rc-msg rc-msg-reply">
                            <div className="rc-msg-bubble rc-msg-working">
                                <FontAwesomeIcon icon={faSpinner} spin /> Building your report…
                            </div>
                        </div>
                    )}
                </div>

                <div className="rc-chat-input">
                    <textarea
                        rows={2}
                        aria-label="Describe the report you need"
                        placeholder="Describe the report you need…"
                        value={input}
                        disabled={sending}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                send();
                            }
                        }}
                    />
                    <button
                        type="button"
                        className="rc-send-btn"
                        onClick={() => send()}
                        disabled={sending || !input.trim()}
                        aria-label="Send"
                    >
                        <FontAwesomeIcon icon={sending ? faSpinner : faPaperPlane} spin={sending} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AskAi;
